import { JSX, ReactNode, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    DateField,
    Labeled,
    ReferenceField,
    SelectField,
    Show,
    TextField,
    TopToolbar,
    ListButton,
    usePermissions,
    useRecordContext,
    WithRecord,
} from 'react-admin';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Link,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EmailMessage, EmailMessageHeader, SalesConversation } from '../../types/entities';
import { emailMessageDirectionOptions } from '../../defaults/defaults';
import { AdminMiniEmailFrame } from '../edit/components/NokMini/admin-mini/components/AdminMiniEmailFrame';
import { resultRouteFor } from '../edit/components/NokProcessing/failureRoutes';
import { routeFor as salesConversationRoute } from '../lists/SalesConversationList';
import { emailMessageChannels, emailMessageStatusChoices, headersOf, idFromIri } from './email-message-format';
import { checkRobotOrHumanAgain, RescoreResult } from '../edit/components/NokProcessing/nokProcessingApi';
import { permissionManager } from '../permissions/manager';

/**
 * One stored email, read only.
 *
 * The HTML body goes through `AdminMiniEmailFrame`, never straight into the page: it is a sandboxed
 * frame with remote images blanked, because loading a message's tracking pixel records an open
 * against the recipient, and on a mystery shop that is the measurement.
 */

const ShowActions: () => JSX.Element = (): JSX.Element => (
    <TopToolbar>
        <ListButton />
    </TopToolbar>
);

const ShowTitle: () => JSX.Element|null = (): JSX.Element|null => {
    const record: EmailMessage|undefined = useRecordContext<EmailMessage>();

    return record ? <span>{ 'Email ' + record.id }</span> : null;
};

/** The record this message belongs to, as a link, or nothing when it stands alone. */
const BelongsTo: () => JSX.Element = (): JSX.Element => {
    const record: EmailMessage|undefined = useRecordContext<EmailMessage>();
    if (!record) {
        return <></>;
    }

    const links: ReactNode[] = [];
    if (record.mysteryShopperResult) {
        links.push(
            <Link key="ms" component={ RouterLink } to={ resultRouteFor('ms', Number(record.mysteryShopperResult)) }>
                { 'Mystery Shopper result ' + record.mysteryShopperResult }
            </Link>
        );
    }
    const complianceId: string|null = idFromIri(record.complianceSnifferResult, '/hq_api/compliance_sniffer_results/');
    if (complianceId !== null) {
        links.push(
            <Link key="cs" component={ RouterLink } to={ resultRouteFor('cs', Number(complianceId)) }>
                { 'Compliance Sniffer result ' + complianceId }
            </Link>
        );
    }
    const conversationId: string|null = idFromIri(record.salesConversation, '/hq_api/sales_conversations/');
    if (conversationId !== null) {
        links.push(
            // The conversation is worked from the request it belongs to, so the link goes there.
            <ReferenceField
                key="sales"
                record={ { conversationId } }
                source="conversationId"
                reference="sales_conversations"
                link={ false }
            >
                <WithRecord render={ (conversation: SalesConversation): JSX.Element => {
                    const route: string|null = salesConversationRoute(conversation);
                    const label: string = 'Sales conversation ' + conversation.id;

                    return route === null
                        ? <span>{ label }</span>
                        : <Link component={ RouterLink } to={ route }>{ label }</Link>;
                } } />
            </ReferenceField>
        );
    }

    return (
        <Labeled label="Belongs to">
            { links.length === 0
                ? <Typography variant="body2" color="text.secondary">{ 'Nothing' }</Typography>
                : <Box sx={ { display: 'flex', flexDirection: 'column', gap: 0.5 } }>{ links }</Box> }
        </Labeled>
    );
};

/**
 * The Operator's robot-or-human verdict on an inbound Nok reply, and a way to ask for it again.
 *
 * A null score is a reply whose check never came back, which is not the same as one scored as a
 * robot, so it reads "Not scored". The button is offered on a reply that answers a run to anyone
 * who may rescore; HQ-API decides the rest and says why when it refuses. The verdict arrives a few
 * seconds after the Operator takes the check, so nothing refreshes here on success.
 */
const RobotOrHuman: () => JSX.Element = (): JSX.Element => {
    const record: EmailMessage|undefined = useRecordContext<EmailMessage>();
    const { permissions } = usePermissions();
    const [busy, setBusy] = useState<boolean>(false);
    const [result, setResult] = useState<RescoreResult|null>(null);

    const score: number|null = record?.robotOrHumanScore ?? null;
    const answersARun: boolean = !!record?.mysteryShopperResult || !!record?.complianceSnifferResult;
    const canRunAgain: boolean = answersARun && permissionManager.isAllowed(permissions, 'HQ_Data::nok_rescore');

    const runAgain = async (): Promise<void> => {
        if (!record) {
            return;
        }
        setBusy(true);
        setResult(null);
        setResult(await checkRobotOrHumanAgain(Number(record.id)));
        setBusy(false);
    };

    return (
        <Labeled label="Robot or human">
            <Box sx={ { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 } }>
                <Typography variant="body2">
                    { score === null
                        ? 'Not scored'
                        : (record?.isHuman ? 'Human' : 'Robot') + ' (score ' + String(score) + ')' }
                </Typography>
                { canRunAgain && (
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={ busy }
                        onClick={ (): void => { void runAgain(); } }
                    >
                        { busy ? 'Sending' : 'Run robot check again' }
                    </Button>
                ) }
                { result && (
                    <Alert severity={ result.status === 'success' ? 'success' : 'error' }>{ result.message }</Alert>
                ) }
            </Box>
        </Labeled>
    );
};

const Details: () => JSX.Element = (): JSX.Element => {
    const record: EmailMessage|undefined = useRecordContext<EmailMessage>();
    // Only an inbound Nok reply is scored; on anything else the stored defaults mean nothing.
    const scored: boolean = record?.channel !== 'sales' && record?.direction === 'inbound';

    return (
        <Grid container spacing={ 2 }>
            <Grid size={ { xs: 12, md: 6 } }>
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 1 } }>
                    <Labeled label="From"><TextField source="sender" /></Labeled>
                    <Labeled label="To"><TextField source="recipient" /></Labeled>
                    <Labeled label="Date"><DateField source="createdAt" showTime /></Labeled>
                    <Labeled label="Message ID">
                        <TextField source="messageId" sx={ { fontFamily: 'monospace', wordBreak: 'break-all' } } />
                    </Labeled>
                    <BelongsTo />
                </Box>
            </Grid>
            <Grid size={ { xs: 12, md: 6 } }>
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 1 } }>
                    <Labeled label="Channel"><SelectField source="channel" choices={ emailMessageChannels } /></Labeled>
                    <Labeled label="Direction">
                        <SelectField source="direction" choices={ emailMessageDirectionOptions } />
                    </Labeled>
                    <Labeled label="Status">
                        <SelectField source="status" choices={ emailMessageStatusChoices } emptyText="-" />
                    </Labeled>
                    <Labeled label="Delivered"><DateField source="delivered" showTime emptyText="-" /></Labeled>
                    <Labeled label="Opened"><DateField source="opened" showTime emptyText="-" /></Labeled>
                    { record?.sentByName && (
                        <Labeled label="Sent by"><TextField source="sentByName" /></Labeled>
                    ) }
                    { scored && <RobotOrHuman /> }
                </Box>
            </Grid>
        </Grid>
    );
};

const Body: () => JSX.Element = (): JSX.Element => {
    const record: EmailMessage|undefined = useRecordContext<EmailMessage>();

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
            <Typography variant="h6" component="h2">{ record?.subject || '(no subject)' }</Typography>
            <AdminMiniEmailFrame html={ record?.bodyHtml } text={ record?.body } />
            { /* The frame shows the HTML half when there is one, so the plain half is offered
                 separately rather than lost. */ }
            { (record?.bodyHtml ?? '').trim() !== '' && (record?.body ?? '').trim() !== '' && (
                <Accordion disableGutters variant="outlined">
                    <AccordionSummary expandIcon={ <ExpandMoreIcon /> }>{ 'Plain text' }</AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" sx={ { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }>
                            { record?.body }
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            ) }
        </Box>
    );
};

const Headers: () => JSX.Element = (): JSX.Element => {
    const record: EmailMessage|undefined = useRecordContext<EmailMessage>();
    const headers: EmailMessageHeader[] = record ? headersOf(record) : [];

    return (
        <Accordion disableGutters variant="outlined">
            <AccordionSummary expandIcon={ <ExpandMoreIcon /> }>{ 'Headers (' + headers.length + ')' }</AccordionSummary>
            <AccordionDetails>
                { headers.length === 0
                    ? <Typography variant="body2" color="text.secondary">{ 'No headers stored' }</Typography>
                    : (
                        // Fixed layout, or one long `Received` line widens the whole admin.
                        <Table size="small" sx={ { tableLayout: 'fixed', width: '100%' } }>
                            <TableBody>
                                { headers.map((header: EmailMessageHeader, index: number): JSX.Element => (
                                    <TableRow key={ index }>
                                        <TableCell sx={ { width: '28%', verticalAlign: 'top', fontWeight: 600 } }>
                                            { header.key }
                                        </TableCell>
                                        <TableCell sx={ {
                                            fontFamily: 'monospace',
                                            fontSize: '12px',
                                            wordBreak: 'break-all',
                                        } }>
                                            { header.value }
                                        </TableCell>
                                    </TableRow>
                                )) }
                            </TableBody>
                        </Table>
                    ) }
            </AccordionDetails>
        </Accordion>
    );
};

export const EmailMessagesShow: () => JSX.Element = (): JSX.Element => (
    <Show actions={ <ShowActions /> } title={ <ShowTitle /> }>
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2, p: 2 } }>
            <Card variant="outlined">
                <CardContent><Details /></CardContent>
            </Card>
            <Body />
            <Headers />
        </Box>
    </Show>
);
